import { FC, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { Button } from "components/button";
import { Input } from "components/input";
import { ErrorP } from "components/input/input.style";
import { SelectInput } from "components/select";
import { Textarea } from "components/textarea";
import { CheckBoxInput, CheckBoxLabel } from "container/update-school-form/curriculum-checkbox";
import { InputWrapper } from "styles/form";
import { FormFooter } from "styles/form/form";
import { CountriesList } from "utils/countries-list";
import { ISummerCampApplication, ISummerCampForm } from "./i-application-form";
import { genderData, relationData, religionData } from "./summer-camp-data";

const schema = yup.object().shape({
   pupilLastName: yup.string().required("Last name is a required field"),
   pupilOtherName: yup.string().required("Other name is a required field"),
   pupilGender: yup.string().required("Gender is a required field"),
   pupilDOB: yup.string().required("Date of birth is a required field"),
   pupilResidentialAddress: yup.string().required("Residential address is a required field"),
   nationality: yup.string().required("Nationality is a required field"),
   stateOfOrigin: yup.string().required("State of origin is a required field"),
   religion: yup.string().required("Religion is a required field"),
   currentSchool: yup.string().required("Current School is a required field"),
   presentClass: yup.string().required("Present class is a required field"),
   classApplyingTo: yup.string().required("Class applying to is a required field"),
   relationToPupil: yup.string().required("Class is a required field"),
   consent: yup
      .boolean()
      .oneOf([true], "You must certify that the information provided is correct"),
});

const FormStep1: FC<ISummerCampApplication> = ({ setFormBody, formBody, setCurrentStep }) => {
   const [currentStates, setCurrentStates] = useState<any[]>([]);
   const [currentCountries, _setCurrentCountries] = useState(() =>
      CountriesList.map((c) => ({ label: c.name, value: c.name.toLowerCase() }))
   );

   const {
      handleSubmit,
      register,
      formState: { errors },
      control,
      watch,
   } = useForm<ISummerCampForm>({
      resolver: yupResolver(schema),
      mode: "onChange",
   });

   const onSubmit = (values) => {
      delete values?.consent;
      setFormBody(values);
      setCurrentStep(1);
   };

   function handleCurrentStates(countryName) {
      setCurrentStates([]);
      const currentState = CountriesList.find(
         (country) => country.name.toLowerCase() === countryName
      )?.states.map((el) => ({ value: el.name.toLowerCase(), label: el.name }));

      setCurrentStates(currentState as any[]);
   }

   return (
      <form onSubmit={handleSubmit(onSubmit)}>
         <InputWrapper className="grid-2-2 mt-13">
            <Input
               label="First name"
               id="pupilOtherName"
               type="text"
               defaultValue={formBody?.pupilOtherName}
               style={{ height: "4.2rem" }}
               {...register("pupilOtherName")}
               error={errors.pupilOtherName?.message}
               required
            />
            <Input
               label="Last Name"
               id="pupilLastName"
               type="text"
               defaultValue={formBody?.pupilLastName}
               style={{ height: "4.2rem" }}
               {...register("pupilLastName")}
               error={errors.pupilLastName?.message}
               required
            />
         </InputWrapper>
         <InputWrapper className="grid-2-2 mt-13">
            <Input
               label="Present School"
               id="currentSchool"
               type="text"
               defaultValue={formBody?.currentSchool}
               style={{ height: "4.2rem" }}
               {...register("currentSchool")}
               error={errors.currentSchool?.message}
               required
            />

            <Input
               label="Date Of Birth"
               id="pupilDOB"
               type="date"
               defaultValue={formBody?.pupilDOB}
               style={{ height: "4.2rem" }}
               {...register("pupilDOB")}
               error={errors.pupilDOB?.message}
               required
            />
         </InputWrapper>
         <InputWrapper className="grid-2-2 mt-13">
            <Controller
               control={control}
               name="nationality"
               render={({ field: { onChange } }) => (
                  <>
                     <SelectInput
                        label="Nationality"
                        id="nationality"
                        type="text"
                        isClearable
                        placeholder="Nationality"
                        onChange={(val: any) => {
                           onChange(val?.value);
                           handleCurrentStates(val?.value);
                        }}
                        error={errors.nationality?.message}
                        options={currentCountries}
                        style={{ height: "4.2rem" }}
                     />
                  </>
               )}
            />
            <Controller
               control={control}
               name="stateOfOrigin"
               render={({ field: { onChange } }) => (
                  <>
                     <SelectInput
                        label="State of Origin"
                        id="stateOfOrigin"
                        type="text"
                        isClearable
                        placeholder="State of Origin"
                        key={watch().nationality}
                        onChange={(val: any) => onChange(val?.value)}
                        error={errors.stateOfOrigin?.message}
                        options={currentStates}
                        style={{ height: "4.2rem" }}
                     />
                  </>
               )}
            />
         </InputWrapper>

         <InputWrapper className="grid-2-2 mt-13">
            <Input
               label="Present Class"
               id="presentClass"
               type="text"
               defaultValue={formBody?.presentClass}
               style={{ height: "4.2rem" }}
               {...register("presentClass")}
               error={errors.presentClass?.message}
            />
         </InputWrapper>
         <InputWrapper>
            <Controller
               control={control}
               name="relationToPupil"
               defaultValue={formBody?.relationToPupil}
               render={({ field: { onChange } }) => (
                  <>
                     <SelectInput
                        label="Relation To Pupil"
                        id="relationToPupil"
                        onChange={(data) => onChange(data?.value)}
                        placeholder="Relation To Pupil"
                        error={errors.relationToPupil?.message}
                        options={relationData}
                        style={{ height: "4.2rem" }}
                     />
                  </>
               )}
            />
         </InputWrapper>
         <InputWrapper className="grid-2-2 mt-13">
            <div className="stack">
               <Controller
                  control={control}
                  name="pupilGender"
                  defaultValue={formBody?.religion}
                  render={({ field: { onChange } }) => (
                     <>
                        <SelectInput
                           id="pupilGender"
                           label="Gender"
                           onChange={(data) => onChange(data?.value)}
                           placeholder="Select Gender"
                           error={errors.pupilGender?.message}
                           options={genderData}
                           style={{ height: "4.2rem" }}
                        />
                     </>
                  )}
               />
               <Controller
                  control={control}
                  name="religion"
                  defaultValue={formBody?.religion}
                  render={({ field: { onChange } }) => (
                     <>
                        <SelectInput
                           label="Religion"
                           id="religion"
                           onChange={(data) => onChange(data?.value)}
                           placeholder="Select Religion"
                           error={errors.religion?.message}
                           options={religionData}
                           style={{ height: "4.2rem" }}
                        />
                     </>
                  )}
               />
               <div>&nbsp;</div>
            </div>
            <Textarea
               label="Residential Address"
               id="pupilResidentialAddress"
               placeholder="Enter"
               defaultValue={formBody?.pupilResidentialAddress}
               style={{ height: "20rem" }}
               {...register("pupilResidentialAddress")}
               error={errors.pupilResidentialAddress?.message}
            />
         </InputWrapper>
         <InputWrapper>
            <div className="flex-center">
               <CheckBoxInput
                  id="consent"
                  type="checkbox"
                  {...register("consent")}
                  name="consent"
               />
               <CheckBoxLabel htmlFor="consent">
                  I certify that the information above is correct.
               </CheckBoxLabel>
            </div>
            <ErrorP className="error-text">{errors.consent && errors.consent?.message}</ErrorP>
         </InputWrapper>

         <FormFooter>
            <div className="form-btn">
               <Button
                  disabled={Object.keys(errors).length > 0 || Object.values(watch()).length < 1}
                  type="submit"
                  variant="primary"
               >
                  Next
               </Button>
            </div>
         </FormFooter>
      </form>
   );
};

export default FormStep1;
